import { Feature, GeoJsonProperties, MultiPolygon, Polygon } from "geojson";
import { AnchorUnitEnum, RateTypeEnum, PricingConfiguration } from "./pricing-configurator.types";

export interface LocalizedString {
  locale: string;
  text: string;
}

export type PaymentZoneAreaFeatureProperties = GeoJsonProperties & {
  name?: string;
  color?: [number, number, number, number];
};

export type PaymentZoneArea = Feature<Polygon, PaymentZoneAreaFeatureProperties> & {
  id: string;
  geometry: Polygon;
  name?: string;
  color?: [number, number, number, number];
  publishedDate?: string;
  cityId?: string;
};

export type CreatePaymentZoneAreaInput = Omit<PaymentZoneArea, "id">;

export type UpdatePaymentZoneAreaInput = PaymentZoneArea;

export type PaymentZoneAreaProperties = {
  name: string;
  color?: [number, number, number, number];
  publishedDate?: Date | undefined;
};

export type SavePaymentZoneAreaPayload = {
  properties?: PaymentZoneAreaProperties;
  geometry?: MultiPolygon;
  zoneId?: string;
};

export enum ParkingRateStatusEnum {
  DRAFT = "draft",
  ENABLED = "enabled",
  DISABLED = "disabled",
}

export enum ParkingPaymentModeType {
  View = "view",
  AddParkingPayment = "addParkingPayment",
  EditParkingPayment = "editParkingPayment",
  ManagePaymentZoneArea = "managePaymentZoneArea",
  AddPaymentZoneArea = "addPaymentZoneArea",
  EditPaymentZoneArea = "editPaymentZoneArea",
  AddParkingRate = "addParkingRate",
  CityConfig = "cityConfig",
  ManageFeeConfigs = "manageFeeConfigs",
}

export enum TicketProviderEnum {
  Easypark = "easypark",
  Extenso = "extenso",
  ExtensoE2 = "extenso_e2",
  Polyconseil = "polyconseil",
  PrestoPlatform = "presto_platform",
}

export enum RightsProviderEnum {
  Easypark = "easypark",
  Extenso = "extenso",
  Polyconseil = "polyconseil",
  PrestoPlatform = "presto_platform",
}

export interface PricingPointType {
  duration: number;
  unit?: AnchorUnitEnum;
  priceAmount: number;
}

export enum PaidDayOfWeek {
  Monday = "monday",
  Tuesday = "tuesday",
  Wednesday = "wednesday",
  Thursday = "thursday",
  Friday = "friday",
  Saturday = "saturday",
  Sunday = "sunday",
}

export interface PaidTimeRange {
  day: PaidDayOfWeek;
  startTime: string;
  endTime: string;
}

export interface ValidityArea {
  name: string;
  eTicketId?: number;
}

export interface EasyparkArea {
  name: string;
  areaNo: number;
}

export interface TicketProvider {
  provider: TicketProviderEnum;
  /** For EasyPark this is the city's SmartHUB subdomain; opaque vendor id for the others. */
  code: string;
  // EasyPark
  easyparkAreas?: EasyparkArea[];
  easyparkAreaCountryCode?: string;
  // Extenso / Extenso E2
  extensoETicketUrl?: string;
  extensoETicketPassword?: string;
  extensoValidityAreas?: ValidityArea[];
  extensoProfiles?: string[];
  extensoDefaultProfile?: string;
  // Polyconseil
  polyconseilZones?: string[];
  polyconseilSubscriptionPlans?: string[];
  polyconseilPricingCategories?: string[];
  polyconseilTariffCodes?: string[];
  polyconseilValidityAreas?: string[];
  // PrestoPlatform
  prestoPlatformPointsOfSale?: string[];
}

export interface RightsProvider {
  provider: RightsProviderEnum;
  /** For EasyPark this is the city's SmartHUB subdomain; opaque vendor id for the others. */
  code: string;
  // Extenso
  extensoProfiles?: string[];
  extensoValidityAreas?: string[];
  // Polyconseil
  polyconseilSubscriptionPlans?: string[];
  polyconseilValidityAreas?: string[];
  // PrestoPlatform
  prestoPlatformUrbanParkingSiteIds?: string[];
}

export interface CityConfig {
  ticketProvider?: TicketProvider;
  rightsProvider?: RightsProvider;
}

/**
 * Update body: an explicit null clears a configured provider, whereas an
 * omitted key would leave the stored one untouched.
 */
export interface CityConfigUpdatePayload {
  ticketProvider: TicketProvider | null;
  rightsProvider: RightsProvider | null;
}

export type RateShortcut = {
  duration: number;
  unit: AnchorUnitEnum;
};

export enum FreeSessionWindowTypeEnum {
  Rolling = "rolling",
  Calendar = "calendar",
}

/**
 * SHORT rates only: the first `minutes` of a session are free, granted at most
 * once per usage window per license plate. Omit `periodMinutes` for once per
 * session with no cross-session limit; `calendar` windows are local-midnight
 * aligned, so `periodMinutes` must divide a day evenly.
 */
export interface FreeSession {
  minutes: number;
  periodMinutes?: number;
  windowType?: FreeSessionWindowTypeEnum;
}

type ProviderParkingVariant =
  | { provider: TicketProviderEnum.Easypark; easyparkAreaNo: number }
  | { provider: TicketProviderEnum.Extenso; extensoValidityArea: string; extensoProfile?: string }
  | { provider: TicketProviderEnum.ExtensoE2; extensoValidityArea: string; extensoProfile?: string }
  | {
      provider: TicketProviderEnum.Polyconseil;
      polyconseilZone: string;
      polyconseilTariffCode: string;
      polyconseilPricingCategory: string;
    }
  | {
      provider: TicketProviderEnum.PrestoPlatform;
      prestoPlatformUrbanParkingSiteId: string;
      prestoPlatformTariffType?: string;
      prestoPlatformPointOfSale?: string;
      prestoPlatformParkingPermits?: string[];
    };

/**
 * When `requiresRight` is set, only plates holding a valid parking right matching
 * this rate's profile and zone may select and pay it; everyone else is refused.
 * Absent means the rate is open to all.
 */
export type ProviderParking = ProviderParkingVariant & { requiresRight?: boolean };

export interface ParkingRateType {
  id?: string;
  cityId: string;
  logicalRateId?: string;
  name: LocalizedString[];
  detail: LocalizedString[];
  zoneName: string;
  zoneId: string;
  startDate: string;
  endDate?: string | null;
  shortcuts?: RateShortcut[];
  freeMonths?: number[];
  freeOnHolidays?: boolean;
  /** Omit the key to leave a stored free session untouched; `null` is not part of the contract. */
  freeSession?: FreeSession;
  extendable?: boolean;
  stoppable?: boolean;
  rateAmount: number;
  rateUnit: string;
  userProfile: string;
  status?: ParkingRateStatusEnum;
  version?: number;
  restoredFromVersion?: number;
  publishedAt?: string;
  publishedBy?: string;
  compiledPricingPoints?: PricingPointType[];
  pricingConfiguration?: PricingConfiguration;
  paidTimeRanges?: PaidTimeRange[];
  rateType?: RateTypeEnum;
  /** Reference to an ENABLED fee config version; null clears it, absent keeps the stored one. */
  feeConfigId?: string | null;
  providerParking?: ProviderParking;
}
